/**
 * The cart of a visitor who is not logged in, kept in this browser (ADR-0005 in the platform repo).
 * Only product ids, quantities and the price the visitor saw — nothing personal. Prices always come from the
 * server; the seen price only lets the cart say «цена изменилась». After login the items are merged into the
 * server cart and this store is cleared.
 */
export interface GuestItem {
  productId: string;
  quantity: number;
  /** Tiyin, as the server priced the line when the visitor last chose the quantity. */
  seenPrice?: number;
}

const KEY = "ishonch.cart.v1";
/** Product id of the API (ADR-0007). Carts saved before it held plain UUIDs: such lines are dropped. */
const PRODUCT_ID = /^prd_[0-9a-f]{32}$/;
export const MAX_QUANTITY = 5;
export const MAX_LINES = 20;

let items: readonly GuestItem[] = read();
const listeners = new Set<() => void>();

function read(): GuestItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (i): i is GuestItem =>
          typeof i === "object" &&
          i !== null &&
          typeof (i as GuestItem).productId === "string" &&
          PRODUCT_ID.test((i as GuestItem).productId) &&
          typeof (i as GuestItem).quantity === "number",
      )
      .map((i) => ({
        productId: i.productId,
        quantity: clamp(i.quantity),
        ...(typeof i.seenPrice === "number" && i.seenPrice > 0 ? { seenPrice: i.seenPrice } : {}),
      }))
      .filter((i) => i.quantity > 0)
      .slice(0, MAX_LINES);
  } catch {
    return []; // storage unavailable or corrupted: start empty
  }
}

function write(next: readonly GuestItem[]) {
  items = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // ignore: the cart just won't survive a reload
  }
  listeners.forEach((listener) => {
    listener();
  });
}

function clamp(quantity: number): number {
  return Math.max(0, Math.min(MAX_QUANTITY, Math.floor(quantity)));
}

export const guestCart = {
  getItems: (): readonly GuestItem[] => items,

  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    // other tabs of the same browser
    const onStorage = (event: StorageEvent) => {
      if (event.key === KEY) {
        items = read();
        listener();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      listeners.delete(listener);
      window.removeEventListener("storage", onStorage);
    };
  },

  /**
   * Sets a product's quantity; 0 removes it. New products go to the end, up to MAX_LINES. `currentPrice` (tiyin) is
   * the price shown when the visitor chose the quantity: from now on that is the price they have seen.
   */
  setQuantity(productId: string, quantity: number, currentPrice?: number) {
    const q = clamp(quantity);
    const exists = items.some((i) => i.productId === productId);
    const seen = currentPrice === undefined ? {} : { seenPrice: currentPrice };
    if (q === 0) {
      write(items.filter((i) => i.productId !== productId));
    } else if (exists) {
      write(items.map((i) => (i.productId === productId ? { ...i, quantity: q, ...seen } : i)));
    } else if (items.length < MAX_LINES) {
      write([...items, { productId, quantity: q, ...seen }]);
    }
  },

  /** Remembers the first price the visitor saw for lines added before it was known. */
  rememberPrices(prices: ReadonlyMap<string, number>) {
    if (!items.some((i) => i.seenPrice === undefined && prices.has(i.productId))) return;
    write(
      items.map((i) =>
        i.seenPrice === undefined && prices.has(i.productId)
          ? { ...i, seenPrice: prices.get(i.productId) }
          : i,
      ),
    );
  },

  clear() {
    write([]);
  },
};
